/**
 * Offline Indicator Component (Web)
 *
 * Displays a banner when the user is disconnected from the internet.
 * Uses the browser's navigator.onLine API and online/offline events.
 */

import { useState, useEffect } from 'react';
import { WifiOff, X } from 'lucide-react';

export function OfflineIndicator() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      setIsDismissed(false);
    };

    const handleOffline = () => {
      setIsOffline(true);
      setIsDismissed(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Don't show if online or if dismissed
  if (!isOffline || isDismissed) {
    return null;
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className="
        fixed bottom-4 left-4 right-4 z-50
        mx-auto max-w-md
        flex items-center gap-3
        px-4 py-3 rounded-lg
        bg-amber-50 border border-amber-200
        text-amber-800
        shadow-warm-md
        animate-slide-up
      "
    >
      <WifiOff className="h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
      <div className="flex-1">
        <p className="text-sm font-medium">You're offline</p>
        <p className="text-xs text-amber-700">
          Changes will sync when you reconnect.
        </p>
      </div>
      <button
        type="button"
        onClick={() => setIsDismissed(true)}
        className="
          p-1 rounded
          text-amber-600 hover:text-amber-800 hover:bg-amber-100
          transition-colors
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500
        "
        aria-label="Dismiss"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/**
 * Hook for checking online status
 */
export function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return isOnline;
}
